import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { generatePhoto, getPhotoUrl } from '@/utils/api';
import { submitLead } from '@/utils/api';
import { blobToBase64 } from '@/utils/imageUtils';
import { recordAttempt } from '@/utils/rateLimit';
import { toast } from 'sonner';

export default function PhotoGenerate({ imageBlobs, userName, leadData, onGenerated, onError, onLimitReached }) {
  const [status, setStatus] = useState('generating');

  useEffect(() => {
    let cancelled = false;
    let wakeLock = null;

    const acquireWakeLock = async () => {
      if ('wakeLock' in navigator) {
        try {
          wakeLock = await navigator.wakeLock.request('screen');
        } catch {
          // Silently ignore — not critical
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !wakeLock && !cancelled) {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    acquireWakeLock();

    const run = async () => {
      try {
        // Convert all blobs to base64
        const base64Array = await Promise.all(
          imageBlobs.map((blob) => blobToBase64(blob))
        );

        const result = await generatePhoto(base64Array, userName, leadData.email);

        if (cancelled) return;

        recordAttempt(leadData.email);

        const fullPhotoUrl = getPhotoUrl(result.photoId);
        const absolutePhotoUrl =
          typeof window !== 'undefined' && fullPhotoUrl.startsWith('/')
            ? `${window.location.origin}${fullPhotoUrl}`
            : fullPhotoUrl;

        // Fire-and-forget lead submission
        submitLead({
          type: 'photo',
          name: leadData.name,
          phone: leadData.phone,
          email: leadData.email,
          photoUrl: absolutePhotoUrl,
          timestamp: new Date().toISOString(),
        }).catch(() => {});

        onGenerated(fullPhotoUrl, result.photoId);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 429 || err.code === 'RATE_LIMIT') {
          if (onLimitReached) onLimitReached();
          return;
        }
        setStatus('error');
        toast.error(err.message || 'Erro na geração. Tente outra foto.');
      }
    };

    run();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) {
        wakeLock.release().catch(() => {});
        wakeLock = null;
      }
    };
  }, []);

  if (status === 'error') {
    return (
      <div className="px-6 py-8 text-center animate-fade-in" data-testid="photo-generate-error">
        <div
          className="mx-auto mb-6 w-20 h-20 rounded-full flex items-center justify-center"
          style={{
            background: 'rgba(217, 64, 64, 0.12)',
            border: '2px solid var(--c21-error)',
          }}
        >
          <span className="text-2xl" style={{ color: 'var(--c21-error)' }}>!</span>
        </div>
        <h3 className="font-headline text-lg mb-2 text-white">
          Não conseguimos gerar a imagem
        </h3>
        <p className="font-body text-sm mb-6" style={{ color: '#A3A3A3' }}>
          Tente com outras fotos!
        </p>
        <button
          onClick={onError}
          className="btn-gold w-full"
          data-testid="photo-generate-retry-btn"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 py-8 text-center animate-fade-in" data-testid="photo-generate-loading">
      {/* Spinning Icon */}
      <div className="mx-auto mb-8 relative" style={{ width: 80, height: 80 }}>
        <div
          className="absolute inset-0 rounded-full animate-spin-slow"
          style={{
            border: '3px solid transparent',
            borderTop: '3px solid #B5985A',
            borderRight: '3px solid #B5985A',
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2
            size={32}
            strokeWidth={1.5}
            style={{ color: '#B5985A' }}
            className="animate-spin"
          />
        </div>
      </div>

      <h3 className="font-headline text-lg mb-3 text-white">
        A IA está a criar a sua foto no kart...
      </h3>

      <p className="font-body text-sm mb-2" style={{ color: '#A3A3A3' }}>
        Estamos a gerar a foto. Pode demorar até 60 segundos.
      </p>
      <p className="font-body text-xs mb-6" style={{ color: '#B5985A' }}>
        Mantenha o ecrã ligado e não saia desta página.
      </p>

      {/* Indeterminate progress */}
      <div className="progress-indeterminate mx-auto" style={{ maxWidth: '200px' }} />
    </div>
  );
}
